import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { nowUTC } from '../utils/dateUtils';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  Checkbox,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Add as AddIcon,
  DataObject as DataObjectIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useKeycloak } from '../context/KeycloakContext';
import codexService from '../services/codexService';
import codexTypeService from '../services/codexTypeService';
import processService from '../services/processService';
import { format } from 'date-fns';
import api, { API_URL, API_BASE_PATH } from '../services/apiConfig';

const NewCodex = () => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const userInfo = keycloak.tokenParsed || {};
  
  // Stati per lo stepper
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Seleziona Tipo', 'Carica Files', 'Modifica Attributi', 'Conferma'];
  
  // Stati per i dati
  const [codexTypes, setCodexTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [codexDescription, setCodexDescription] = useState('');
  const [files, setFiles] = useState([]);
  const [metadata, setMetadata] = useState({});
  const [processName, setProcessName] = useState('');
  const [availableProcesses, setAvailableProcesses] = useState([]);
  
  // Stati UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Add these new state variables
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [selectedJsonField, setSelectedJsonField] = useState(null);
  
  // Carica i tipi di codex all'inizializzazione
  useEffect(() => {
    fetchCodexTypes();
  }, []);
  
  // Funzione per caricare i tipi di codex
  const fetchCodexTypes = async () => {
    try {
      setLoading(true);
      const response = await codexTypeService.getAll();
      setCodexTypes(response);
      setLoading(false);
    } catch (err) {
      console.error("Errore nel caricamento dei tipi di codex:", err);
      setError("Impossibile caricare i tipi di codex. Riprova più tardi.");
      setLoading(false);
    }
  };
  
  // Gestione cambio del tipo di codex
  const handleTypeChange = async (event) => {
    const typeId = event.target.value;
    setSelectedType(typeId);
    
    try {
      // Carica i processi disponibili per questo tipo
      const processes = await processService.getProcessesByCodexType(typeId);
      setAvailableProcesses(processes);
      
      // Seleziona automaticamente il primo processo disponibile
      if (processes && processes.length > 0) {
        setProcessName(processes[0].id);
      }
    } catch (err) {
      console.error("Errore nel caricamento dei processi:", err);
      setError("Impossibile caricare i processi disponibili.");
    }
    
    // Trova il tipo selezionato
    const type = codexTypes.find(t => t.id === typeId);
    
    if (type && type.attributes && type.attributes.length > 0) {
      // Inizializza i attributi con i campi del tipo di codex
      const initialMetadata = {};
      type.attributes.forEach(field => {
        // Assegna valori di default in base al tipo definito nello schema
        const schemaType = field.schema?.type || 'string';
        switch(schemaType) {
          case 'string':
            initialMetadata[field.code] = '';
            break;
          case 'number':
            initialMetadata[field.code] = 0;
            break;
          case 'boolean':
            initialMetadata[field.code] = false;
            break;
          case 'date':
            // Use UTC date for consistency with backend
            initialMetadata[field.code] = nowUTC().split('T')[0];
            break;
          default:
            initialMetadata[field.code] = '';
        }
      });
      setMetadata(initialMetadata);
    }
  };
  
  // Gestione caricamento file
  const handleFileUpload = (event) => {
    const newFiles = Array.from(event.target.files);
    setFiles(prevFiles => [...prevFiles, ...newFiles]);
  };
  
  // Gestione rimozione file
  const handleDeleteFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };
  
  // Gestione cambio attributi
  const handleMetadataChange = (key, value) => {
    setMetadata(prevMetadata => ({
      ...prevMetadata,
      [key]: value
    }));
  };
  
  // Gestione cambio nome processo
  const handleProcessNameChange = (event) => {
    setProcessName(event.target.value);
  };
  
  // Gestione step successivo
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  // Gestione step precedente
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  // Gestione reset
  const handleReset = () => {
    setActiveStep(0);
    setSelectedType('');
    setCodexDescription('');
    setFiles([]);
    setMetadata({});
    setProcessName('');
    setSuccess(false);
    setError(null);
  };
  
  // Gestione invio dati
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Validation checks
      if (!selectedType) {
        setError("Seleziona un tipo di codex");
        setLoading(false);
        return;
      }
      
      if (files.length === 0) {
        setError("Carica almeno un file");
        setLoading(false);
        return;
      }
      
      if (!processName) {
        setError("Seleziona un processo");
        setLoading(false);
        return;
      }

      // Step 1: Create Codex
      const codexData = {
        codexType: selectedType,
        code: `CODEX-${new Date().getTime()}`,
        description: codexDescription || "Codex creato da interfaccia utente",
        owner: userInfo.preferred_username || 'system',
        attributeValues: metadata
      };

      let codexResponse;
      try {
        const response = await api.post('/codexes', codexData);
        codexResponse = response.data;
      } catch (error) {
        throw new Error('Errore nella creazione del codex: ' + error.message);
      }

      // Step 2: Upload Files
      try {
        const uploadPromises = files.map(async (file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('codexId', codexResponse.id);

          const response = await api.post('/codex-files', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          return response.data;
        });

        await Promise.all(uploadPromises);
      } catch (error) {
        // Rollback: First delete all associated files
        try {
          const filesResponse = await api.get(`/codexes/${codexResponse.id}/files`);
          const deleteFilePromises = filesResponse.data.map(file => 
            api.delete(`/codex-files/${file.id}`)
          );
          await Promise.all(deleteFilePromises);
          
          // Then delete the codex
          await api.delete(`/codexes/${codexResponse.id}`);
        } catch (rollbackError) {
          console.error('Errore durante il rollback:', rollbackError);
        }
        throw new Error('Errore nel caricamento dei file: ' + error.message);
      }

       // Step 2: Create Process
       let processResponse;
       try {
         const resultMessage = {
           id: null,  // Will be generated by backend
           processId: processName,  // This is the process type ID
           codexId: codexResponse.id,
           autoStart: true,  // Auto start the process after creation
           createdBy: userInfo.preferred_username || 'system',
           createdAt: nowUTC()  // Use UTC timestamp for consistency with backend
         };
 
         const response = await api.post('/codex-processes', resultMessage);
         processResponse = response.data;
       } catch (error) {
         throw new Error('Errore nella creazione del processo: ' + error.message);
       }

      setSuccess(true);
      setLoading(false);
      // Show preparation message
      setActiveStep(steps.length);
    } catch (err) {
      console.error("Errore nella creazione del codex:", err);
      setError(err.message || "Impossibile creare il codex. Riprova più tardi.");
      setLoading(false);
    }
  };
  
  // Rendering dei diversi step
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderTypeSelection();
      case 1:
        return renderFileUpload();
      case 2:
        return renderMetadataEditor();
      case 3:
        return renderConfirmation();
      default:
        return null;
    }
  };
  
  // Step 1: Selezione tipo
  const renderTypeSelection = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Seleziona il tipo di documento
        </Typography>
        
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Tipo Codex</InputLabel>
          <Select
            value={selectedType}
            onChange={handleTypeChange}
            label="Tipo Codex"
            renderValue={(selected) => {
              const type = codexTypes.find(t => t.id === selected);
              return type ? (
                <Chip 
                  label={type.code} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              ) : 'Seleziona un tipo';
            }}
          >
            {codexTypes.map(type => (
              <MenuItem key={type.id} value={type.id}>
                <Chip 
                  label={type.code} 
                  size="small" 
                  color="primary"
                  variant="outlined"
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        
        {selectedType && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1">
              Descrizione del tipo:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {codexTypes.find(t => t.id === selectedType)?.description || 'Nessuna descrizione disponibile'}
            </Typography>
          </Box>
        )}

      </Box>
    );
  };
  
  // Step 2: Upload dei file
  const renderFileUpload = () => {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Carica i file
        </Typography>
        
        <Box sx={{ mt: 2, mb: 3 }}>
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            sx={{ mb: 2 }}
          >
            Seleziona File
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload}
            />
          </Button>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              File selezionati ({files.length})
            </Typography>
            
            {files.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nessun file selezionato
              </Typography>
            ) : (
              <List>
                {files.map((file, index) => (
                  <ListItem key={index} divider>
                    <DescriptionIcon sx={{ mr: 2 }} />
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024).toFixed(2)} KB`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" onClick={() => handleDeleteFile(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Box>
    );
  };
  
  // Step 3: Editor dei attributi
  const renderMetadataEditor = () => {
    const selectedTypeObj = codexTypes.find(t => t.id === selectedType);
    // Filter visible attributes and sort by position
    const visibleAttributes = selectedTypeObj?.attributes?.filter(attr => attr.isVisible !== false) || [];
    const sortedAttributes = [...visibleAttributes].sort((a, b) => (a.position || 0) - (b.position || 0));
    const requiredAttributes = sortedAttributes.filter(attr => attr.isRequired);
    const optionalAttributes = sortedAttributes.filter(attr => !attr.isRequired);
    
    return (
      <Box sx={{ p: 3 }}>
        
        <TextField
          fullWidth
          label="Descrizione del Codex"
          value={codexDescription}
          onChange={(e) => setCodexDescription(e.target.value)}
          placeholder="Inserisci una descrizione per questo codex..."
          sx={{ mt: 2, mb: 2 }}
        />

        <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
          <InputLabel>Nome del Processo *</InputLabel>
          <Select
            value={processName}
            onChange={handleProcessNameChange}
            label="Nome del Processo"
            required
          >
            {availableProcesses.map((process) => (
              <MenuItem key={process.id} value={process.id}>
                {process.code}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="h6" gutterBottom>
          Modifica attributi
        </Typography>
       
        {requiredAttributes.length === 0 && optionalAttributes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" paragraph>
            Nessun attributo da modificare
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" paragraph>
            Compila gli attributi per questo tipo di documento ({selectedTypeObj?.code || ''}).
            {requiredAttributes.length > 0 && ` I campi contrassegnati con * sono obbligatori.`}
          </Typography>
        )}

        {/* Required attributes section */}
        {requiredAttributes.length > 0 && (
        <List>
          {requiredAttributes.map((attr) => (
            <ListItem key={attr.code} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
              {renderMetadataField(attr)}
              {attr !== requiredAttributes[requiredAttributes.length - 1]}
            </ListItem>
          ))}
        </List>
        )}

        {/* Optional attributes section */}
        {optionalAttributes.length > 0 && (
        <List>
          {optionalAttributes.map((attr) => (
            <ListItem key={attr.code} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
              {renderMetadataField(attr)}
              {attr !== optionalAttributes[optionalAttributes.length - 1]}
            </ListItem>
          ))}
        </List>
        )}

       
      </Box>
    );
  };
  
  // Renderizza un campo di attributi in base al tipo
  const renderMetadataField = (field) => {
    const { code, description, dataType, schema } = field;
    const value = metadata[code] || '';
    const fieldType = dataType || (schema?.type || 'string');
    const required = field.isRequired || false;
    
    const handleChange = (newValue) => {
      setMetadata(prevMetadata => ({
        ...prevMetadata,
        [code]: newValue
      }));
    };
    
    // Open JSON preview dialog
    const handleOpenJsonPreview = () => {
      setSelectedJsonField({
        code,
        description,
        schema,
        value: metadata[code] || (typeof value === 'string' && value.trim() ? JSON.parse(value) : {})
      });
      setJsonPreviewOpen(true);
    };
    
    // Helper to add visual cues for required fields
    const fieldLabel = required ? `${description} *` : description;
    
    switch (fieldType.toLowerCase()) {
      case 'string':
        return (
          <TextField
            key={`${code}-field`}
            label={fieldLabel}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            fullWidth
            required={required}
            margin="normal"
            variant="outlined"
          />
        );
      case 'number':
        return (
          <TextField
            key={`${code}-field`}
            label={fieldLabel}
            value={value}
            onChange={(e) => handleChange(Number(e.target.value))}
            fullWidth
            required={required}
            margin="normal"
            type="number"
            InputProps={{
              inputProps: { 
                min: schema?.minimum, 
                max: schema?.maximum,
                step: "any"
              }
            }}
            variant="outlined"
            helperText={schema?.description || ''}
          />
        );
      case 'boolean':
        return (
          <FormControlLabel
            key={`${code}-field`}
            control={
              <Switch
                checked={Boolean(value)}
                onChange={(e) => handleChange(e.target.checked)}
                color="primary"
              />
            }
            label={fieldLabel}
            sx={{ mt: 2, mb: 1, display: 'block' }}
          />
        );
      case 'date':
        return (
          <TextField
            key={`${code}-field`}
            label={fieldLabel}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            fullWidth
            required={required}
            margin="normal"
            type="date"
            InputLabelProps={{ shrink: true }}
            variant="outlined"
          />
        );
      case 'select':
        const options = field.options || [];
        return (
          <FormControl 
            key={`${code}-field`}
            fullWidth 
            margin="normal" 
            required={required}
            variant="outlined"
          >
            <InputLabel>{fieldLabel}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleChange(e.target.value)}
              label={fieldLabel}
            >
              {options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {schema?.description && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {schema.description}
              </Typography>
            )}
          </FormControl>
        );
      case 'object':
      case 'json':
        return (
          <Box key={`${code}-field`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mt: 2, mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {fieldLabel} {required && <Chip size="small" label="Required" color="primary" />}
              </Typography>
              <Button
                size="small"
                startIcon={<DataObjectIcon />}
                variant="outlined"
                onClick={handleOpenJsonPreview}
              >
                Visualizza JSON
              </Button>
            </Box>
            
            {schema?.description && (
              <Typography variant="body2" color="text.secondary" paragraph>
                {schema.description}
              </Typography>
            )}
            
            <TextField
              multiline
              rows={5}
              fullWidth
              placeholder="Inserisci qui i dati JSON"
              value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  handleChange(parsed);
                } catch (err) {
                  // Se non è un JSON valido, salvalo come stringa
                  handleChange(e.target.value);
                }
              }}
              variant="outlined"
              InputProps={{
                sx: { fontFamily: 'monospace' }
              }}
              helperText="Inserisci dati in formato JSON valido"
            />
          </Box>
        );
      default:
        return (
          <TextField
            key={`${code}-field`}
            label={fieldLabel}
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            fullWidth
            required={required}
            margin="normal"
            variant="outlined"
          />
        );
    }
  };
  
  // Step 4: Conferma
  const renderConfirmation = () => {
    const selectedTypeObj = codexTypes.find(t => t.id === selectedType);
    const selectedProcess = availableProcesses.find(p => p.id === processName);
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Conferma i dati
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Tipo di documento
                </Typography>
                <Typography variant="body1">
                  {selectedTypeObj?.code || 'Non selezionato'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Nome del processo
                </Typography>
                <Typography variant="body1">
                  {selectedProcess?.code || 'Non specificato'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Descrizione
                </Typography>
                <Typography variant="body1">
                  {codexDescription || 'Nessuna descrizione specificata'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  File selezionati ({files.length})
                </Typography>
                {files.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nessun file selezionato
                  </Typography>
                ) : (
                  <List dense>
                    {files.map((file, index) => (
                      <ListItem key={index}>
                        <DescriptionIcon sx={{ mr: 1, fontSize: 20 }} />
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(2)} KB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Attributi
                </Typography>
                <List dense>
                  {Object.entries(metadata).map(([key, value], index) => {
                    const fieldDef = selectedTypeObj?.attributes?.find(f => f.code === key);
                    let displayValue = value;
                    const fieldType = fieldDef?.schema?.type || 'string';
                    
                    if (fieldType === 'boolean') {
                      displayValue = value ? 'Sì' : 'No';
                    } else if (fieldType === 'date' && value) {
                      try {
                        displayValue = format(new Date(value), 'dd/MM/yyyy');
                      } catch (e) {
                        console.error('Error formatting date:', e);
                      }
                    }

                    if (fieldType === 'object' || fieldType === 'json') {
                      return (
                        <ListItem key={index} divider={index < Object.keys(metadata).length - 1} sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
                          <Box sx={{ width: '100%', mb: 1 }}>
                            <Typography variant="subtitle2" color="text.primary">
                              {fieldDef?.description || key}
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100%', bgcolor: 'grey.50', borderRadius: 1, p: 2 }}>
                            <JsonTreeNode 
                              value={displayValue} 
                              expandAll={false}
                            />
                          </Box>
                        </ListItem>
                      );
                    }
                    
                    return (
                      <ListItem key={index} divider={index < Object.keys(metadata).length - 1}>
                        <ListItemText
                          primary={fieldDef?.description || key}
                          secondary={displayValue !== undefined ? String(displayValue) : 'Non specificato'}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  // Pulsanti di navigazione
  const renderNavigationButtons = () => {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          disabled={activeStep === 0 || loading}
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
        >
          Indietro
        </Button>
        
        <Box>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Salvataggio...' : 'Salva'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              endIcon={<ArrowForwardIcon />}
              disabled={
                (activeStep === 0 && !selectedType) ||
                (activeStep === 1 && files.length === 0) ||
                (activeStep === 2 && !processName)
              }
            >
              Avanti
            </Button>
          )}
        </Box>
      </Box>
    );
  };
  
  // JSON Preview Dialog
  const renderJsonPreviewDialog = () => {
    return (
      <Dialog
        open={jsonPreviewOpen}
        onClose={() => setJsonPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DataObjectIcon color="primary" sx={{ mr: 1 }} />
            {selectedJsonField?.description || 'JSON Editor'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedJsonField && (
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedJsonField.code}
              </Typography>
              
              <GraphicalJsonViewer 
                data={selectedJsonField.value} 
                filename={`${selectedJsonField.code}.json`} 
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  // Rendering principale
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Nuovo Codex
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Codex creato con successo!
        </Alert>
      )}
      
      <Paper sx={{ p: 0, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {activeStep === steps.length ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Codex creato con successo!
            </Typography>
            <Typography variant="body1" paragraph>
              Il codex è stato creato e associato al processo.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button 
                onClick={handleReset} 
                startIcon={<AddIcon />}
              >
                Crea un altro Codex
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={() => navigate('/documenti')}
                endIcon={<CheckIcon />}
              >
                Vai ai Documenti
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            {renderStepContent(activeStep)}
            <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
              {renderNavigationButtons()}
            </Box>
          </>
        )}
      </Paper>
      {renderJsonPreviewDialog()}
    </Box>
  );
};

// Function to download JSON file
const downloadJson = (jsonData, filename) => {
  const json = JSON.stringify(jsonData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

// Create a reusable JSON tree node component
const JsonTreeNode = ({ name, value, level = 0, expandAll = false }) => {
  const [expanded, setExpanded] = useState(expandAll);
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  
  const handleToggle = () => {
    setExpanded(!expanded);
  };
  
  const getValueDisplay = () => {
    if (value === null) return <Typography color="text.secondary" component="span">null</Typography>;
    if (typeof value === 'boolean') return <Typography color="error.main" component="span">{value.toString()}</Typography>;
    if (typeof value === 'number') return <Typography color="success.main" component="span">{value}</Typography>;
    if (typeof value === 'string') return <Typography color="primary.main" component="span">"{value}"</Typography>;
    
    // For objects and arrays, show a summary
    if (isArray) return <Typography color="text.secondary" component="span">Array[{value.length}]</Typography>;
    return <Typography color="text.secondary" component="span">Object{'{}'}</Typography>;
  };
  
  return (
    <Box sx={{ ml: level * 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          py: 0.5,
          cursor: isObject ? 'pointer' : 'default',
          "&:hover": { bgcolor: isObject ? 'action.hover' : 'transparent' },
          borderRadius: 1,
        }}
        onClick={isObject ? handleToggle : undefined}
      >
        {isObject && (
          <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
        
        {name !== undefined && (
          <Typography 
            variant="body2" 
            component="span" 
            fontWeight="bold" 
            sx={{ mr: 0.5 }}
          >
            {isArray ? `${name}:` : `"${name}":`}
          </Typography>
        )}
        
        {isObject ? (
          <Box component="span">
            {getValueDisplay()}
          </Box>
        ) : (
          getValueDisplay()
        )}
      </Box>
      
      {isObject && expanded && (
        <Box sx={{ mt: 0.5 }}>
          {isArray ? (
            value.map((item, index) => (
              <JsonTreeNode 
                key={index} 
                name={index}
                value={item} 
                level={level + 1}
                expandAll={expandAll}
              />
            ))
          ) : (
            Object.entries(value).map(([key, val]) => (
              <JsonTreeNode 
                key={key} 
                name={key} 
                value={val} 
                level={level + 1}
                expandAll={expandAll}
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

// Graphical JSON Viewer component with download option
const GraphicalJsonViewer = ({ data, filename = 'schema.json' }) => {
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'raw'
  const [expandAll, setExpandAll] = useState(false);
  
  const copyToClipboard = () => {
    const json = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(json)
      .then(() => {
        console.log('JSON copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy JSON', err);
      });
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Box>
          <Button
            size="small"
            startIcon={viewMode === 'tree' ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setViewMode(viewMode === 'tree' ? 'raw' : 'tree')}
            sx={{ mr: 1 }}
          >
            {viewMode === 'tree' ? 'Raw View' : 'Tree View'}
          </Button>
          
          {viewMode === 'tree' && (
            <Button
              size="small"
              startIcon={expandAll ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              onClick={() => setExpandAll(!expandAll)}
            >
              {expandAll ? 'Collapse All' : 'Expand All'}
            </Button>
          )}
        </Box>
        
        <Box>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={copyToClipboard}
            sx={{ mr: 1 }}
          >
            Copy
          </Button>
          
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadJson(data, filename)}
            color="primary"
            variant="contained"
          >
            Download JSON
          </Button>
        </Box>
      </Box>
      
      <Paper 
        variant="outlined"
        sx={{ 
          p: 2, 
          maxHeight: 400, 
          overflow: 'auto',
          bgcolor: viewMode === 'raw' ? 'grey.50' : 'background.paper',
          fontFamily: viewMode === 'raw' ? 'monospace' : 'inherit'
        }}
      >
        {viewMode === 'tree' ? (
          <JsonTreeNode value={data} expandAll={expandAll} />
        ) : (
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </Paper>
    </Box>
  );
};

export default NewCodex; 